import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { appointmentService, userService, clientService, appointmentSessionsService } from '../services/api';
import { getStatusPipeline } from '../services/statusPipelineService';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, MapPin, User, Phone, Search, Filter, Plus, Edit, Trash2, Eye, CalendarDays, Briefcase, FileText, Info, Play, Square } from 'lucide-react';
import toast from 'react-hot-toast';
import AppointmentCalendar from './AppointmentCalendar';
import ProjectCalendar from './ProjectCalendar.jsx';
import ScheduleBlocks from './ScheduleBlocks.jsx';
import BlockPermissions from './BlockPermissions.jsx';
import ConfirmModal from './ConfirmModal';

const Appointments = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Estados para filtros e paginação
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    responsible_user_id: '',
    client_id: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'calendar', 'projects' ou 'blocks'
  const [calendarUserId, setCalendarUserId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [endSessionConfirm, setEndSessionConfirm] = useState(null);

  // Buscar agendamentos
  const { data: appointmentsData, isLoading, error, refetch } = useQuery({
    queryKey: ['appointments', filters],
    queryFn: () => appointmentService.getAppointments(filters),
    keepPreviousData: true,
    staleTime: 30000,
    retry: 1,
    onError: (error) => {
      console.error('❌ Erro ao buscar agendamentos:', error);
    }
  });

  // Recarregar dados quando voltar para a visualização de lista
  useEffect(() => {
    if (viewMode === 'list') {
      refetch();
    }
  }, [viewMode, refetch]);

  // Buscar usuários para filtro de responsável
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getUsers({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Normalizar lista de usuários para filtros e calendário
  const users = useMemo(() => {
    if (!usersData) return [];

    if (Array.isArray(usersData?.data?.users)) {
      return usersData.data.users;
    }

    if (Array.isArray(usersData?.data?.data?.users)) {
      return usersData.data.data.users;
    }

    if (Array.isArray(usersData?.data)) {
      return usersData.data;
    }

    console.warn('Formato de dados de usuários não reconhecido em Appointments:', usersData);
    return [];
  }, [usersData]);

  // Buscar clientes para filtro
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Buscar pipeline de status para agendamentos
  const { data: appointmentStatusPipeline } = useQuery({
    queryKey: ['status-pipeline', 'appointment'],
    queryFn: () => getStatusPipeline('APPOINTMENT').then(res => res.data?.data?.status_config),
    staleTime: 5 * 60 * 1000,
  });

  // Função para obter próximos statuses possíveis
  const getNextAppointmentStatuses = (currentStatus) => {
    if (!appointmentStatusPipeline || !appointmentStatusPipeline[currentStatus]) return [];
    return appointmentStatusPipeline[currentStatus].next || [];
  };

  // Função para formatar nome do status
  const formatAppointmentStatusName = (status) => {
    const statusNames = {
      'SCHEDULED': 'Agendado',
      'IN_PROGRESS': 'Em Andamento',
      'COMPLETED': 'Concluído',
      'CANCELLED': 'Cancelado',
      'NO_SHOW': 'Não Compareceu'
    };
    return statusNames[status] || status;
  };

  // Mutação para atualizar status
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => appointmentService.updateAppointmentStatus(id, status),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['surveys']);

      // Verificar se um Levantamento foi criado automaticamente
      // Isso acontece quando o status é COMPLETED e o tipo é LEVANTAMENTO
      if (variables.appointment_type === 'LEVANTAMENTO' && variables.status === 'COMPLETED') {
        // O backend pode retornar os dados do levantamento criado
        const surveyCode = data?.data?.survey_code;
        if (surveyCode) {
          toast.success(`Levantamento criado automaticamente! Código: ${surveyCode}`);
        } else {
          toast.success('Status atualizado com sucesso! Levantamento criado automaticamente.');
        }
      } else {
        toast.success('Status atualizado com sucesso!');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar status');
    },
  });

  // Mutação para deletar agendamento
  const deleteMutation = useMutation({
    mutationFn: (id) => appointmentService.deleteAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      toast.success('Agendamento deletado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar agendamento');
    },
  });

  // Mutação para iniciar sessão de atendimento
  const startSessionMutation = useMutation({
    mutationFn: (data) => appointmentSessionsService.startSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      toast.success('Atendimento iniciado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar atendimento');
    },
  });

  // Mutação para encerrar sessão de atendimento
  const endSessionMutation = useMutation({
    mutationFn: (data) => appointmentSessionsService.endSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['surveys']);
      toast.success('Atendimento encerrado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao encerrar atendimento');
    },
  });

  // Função para atualizar filtros
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset para primeira página ao filtrar
    }));
  };

  // Função para limpar filtros
  const clearFilters = () => {
    setSelectedMonth('');
    setFilters({
      page: 1,
      limit: 10,
      status: '',
      responsible_user_id: '',
      client_id: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  // Função auxiliar para formatar data compatível com HTML5 datetime-local e backend
  const formatDateTime = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Função para filtrar agendamentos do dia atual
  const filterToday = () => {
    setSelectedMonth('');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    
    setFilters(prev => ({
      ...prev,
      date_from: formatDateTime(today),
      date_to: formatDateTime(endOfDay),
      page: 1
    }));
    setViewMode('list');
  };

  // Função para filtrar agendamentos dos próximos 7 dias
  const filterNext7Days = () => {
    setSelectedMonth('');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 7);
    endDate.setHours(23, 59, 59, 999);
    
    setFilters(prev => ({
      ...prev,
      date_from: formatDateTime(today),
      date_to: formatDateTime(endDate),
      page: 1
    }));
    setViewMode('list');
  };

  // Função para filtrar agendamentos dos próximos 15 dias
  const filterNext15Days = () => {
    setSelectedMonth('');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 15);
    endDate.setHours(23, 59, 59, 999);
    
    setFilters(prev => ({
      ...prev,
      date_from: formatDateTime(today),
      date_to: formatDateTime(endDate),
      page: 1
    }));
    setViewMode('list');
  };

  // Função para gerar lista de meses (atual + 12 meses à frente)
  const generateMonthOptions = () => {
    const months = [];
    const monthNames = [
      'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
      'jul', 'ago', 'set', 'out', 'nov', 'dez'
    ];
    
    const today = new Date();
    for (let i = 0; i < 13; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      const monthName = monthNames[monthIndex];
      
      months.push({
        value: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
        label: `${monthName}/${year}`,
        year,
        month: monthIndex
      });
    }
    
    return months;
  };

  // Função para filtrar agendamentos por mês selecionado
  const filterByMonth = (monthValue) => {
    if (!monthValue) {
      // Se não houver seleção, limpar filtros de data
      setFilters(prev => ({
        ...prev,
        date_from: '',
        date_to: '',
        page: 1
      }));
      return;
    }

    const [year, month] = monthValue.split('-');
    const monthNum = parseInt(month, 10) - 1; // JavaScript usa 0-11 para meses
    
    // Primeiro dia do mês
    const startDate = new Date(parseInt(year, 10), monthNum, 1);
    startDate.setHours(0, 0, 0, 0);
    
    // Último dia do mês
    const endDate = new Date(parseInt(year, 10), monthNum + 1, 0);
    endDate.setHours(23, 59, 59, 999);
    
    setFilters(prev => ({
      ...prev,
      date_from: formatDateTime(startDate),
      date_to: formatDateTime(endDate),
      page: 1
    }));
    setViewMode('list');
  };

  const monthOptions = generateMonthOptions();

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day, hour, min] = dateString.split(/[-T:]/).map(Number);
    const localDate = new Date(year, month - 1, day, hour || 0, min || 0, 0);
    return localDate.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day, hour, min] = dateString.split(/[-T:]/).map(Number);
    const localDate = new Date(year, month - 1, day, hour || 0, min || 0, 0);
    return localDate.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (minutes == null) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}min`;
  };

  // Função para obter cor do status
  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'bg-[#d9ead9] text-[#2c5824]';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Função para obter texto do status
  const getStatusText = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'Agendado';
      case 'IN_PROGRESS': return 'Em Andamento';
      case 'COMPLETED': return 'Concluído';
      case 'CANCELLED': return 'Cancelado';
      default: return status;
    }
  };

  // Função para atualizar status
  const handleStatusChange = (appointment, newStatus) => {
    // Armazenar dados do appointment para verificar se é Levantamento
    updateStatusMutation.mutate({
      id: appointment.id,
      status: newStatus,
      appointment_type: appointment.appointment_type
    });
  };

  // Função para deletar agendamento
  const handleDelete = (appointmentId) => {
    setDeleteConfirm(appointmentId);
  };

  // Função para ver detalhes
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetails(true);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Erro ao carregar agendamentos: {error.message}</p>
          <p className="text-sm text-red-500 mt-2">
            Status: {error.response?.status} | 
            Dados: {JSON.stringify(error.response?.data)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agendamentos</h1>
          <p className="text-gray-600">Gerencie os agendamentos do sistema</p>
        </div>
        <button
          onClick={() => navigate('/agendamentos/novo')}
          className="bg-[#49943d] text-white px-4 py-2 rounded-lg hover:bg-[#3a7630] flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Agendamento
        </button>
      </div>

      {/* Toggle de Visualização */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base ${
                viewMode === 'list'
                  ? 'bg-[#49943d] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base ${
                viewMode === 'calendar'
                  ? 'bg-[#49943d] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Calendário
            </button>
            <button
              onClick={() => setViewMode('projects')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base ${
                viewMode === 'projects'
                  ? 'bg-[#49943d] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Projetos
            </button>
            <button
              onClick={() => setViewMode('blocks')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base ${
                viewMode === 'blocks'
                  ? 'bg-[#49943d] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔒 Bloqueios
            </button>
            <button
              onClick={filterToday}
              className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base bg-[#74af41] text-white hover:bg-[#5d8f34]"
              title="Mostrar apenas agendamentos de hoje"
            >
              <Calendar className="w-4 h-4" />
              Hoje
            </button>
            <button
              onClick={filterNext7Days}
              className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base bg-purple-600 text-white hover:bg-purple-700"
              title="Mostrar agendamentos dos próximos 7 dias"
            >
              <Calendar className="w-4 h-4" />
              7 Dias
            </button>
            <button
              onClick={filterNext15Days}
              className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm sm:text-base bg-indigo-600 text-white hover:bg-indigo-700"
              title="Mostrar agendamentos dos próximos 15 dias"
            >
              <Calendar className="w-4 h-4" />
              15 Dias
            </button>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Mês:
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  filterByMonth(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white cursor-pointer min-w-[120px]"
                title="Selecione um mês para filtrar agendamentos"
              >
                <option value="">Todos os meses</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {viewMode === 'calendar' && (
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrar por pessoa:
              </label>
              <select
                value={calendarUserId}
                onChange={(e) => setCalendarUserId(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Todas as pessoas</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Calendário */}
      {viewMode === 'calendar' && (
        <div className="mb-6">
          <AppointmentCalendar
            selectedUserId={calendarUserId || undefined}
            onDateClick={(date, timeSlots) => {
              // Ao clicar em uma data, filtra a lista para mostrar apenas os agendamentos daquele dia
              // Criar data de início do dia (00:00:00) no timezone local
              const startOfDay = new Date(date);
              startOfDay.setHours(0, 0, 0, 0);
              
              // Criar data de fim do dia (23:59:59) no timezone local
              const endOfDay = new Date(date);
              endOfDay.setHours(23, 59, 59, 999);
              
              // Formatar para YYYY-MM-DD HH:MM:SS (formato que PostgreSQL aceita bem)
              const formatDateTime = (d) => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const seconds = String(d.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              };
              
              const dateFromStr = formatDateTime(startOfDay);
              const dateToStr = formatDateTime(endOfDay);
              
              console.log('📅 Filtrando por data:', { dateFromStr, dateToStr, timeSlotsCount: timeSlots.length });
              
              // Atualizar filtros e mudar para lista
              setFilters(prev => ({
                ...prev,
                date_from: dateFromStr,
                date_to: dateToStr,
                page: 1 // Reset para primeira página
              }));
              setViewMode('list');
            }}
          />
        </div>
      )}

      {/* Calendário de Projetos (Agendamentos + Horas de Projeto) */}
      {viewMode === 'projects' && (
        <div className="mb-6">
          <ProjectCalendar
            selectedUserId={calendarUserId || undefined}
            showAppointments={true}
            showProjectHours={true}
          />
        </div>
      )}

      {/* Bloqueios de Agenda */}
      {viewMode === 'blocks' && (
        <div className="mb-6">
          <ScheduleBlocks
            currentUser={user}
            onSwitchTab={(tab) => {
              if (tab === 'permissions') {
                // Mostrar permissões inline
                setViewMode('permissions');
              }
            }}
          />
        </div>
      )}

      {/* Permissões de Bloqueio (GERENCIA) */}
      {viewMode === 'permissions' && (
        <div className="mb-6">
          <BlockPermissions
            currentUser={user}
            onBack={() => setViewMode('blocks')}
          />
        </div>
      )}

      {/* Filtros (apenas na visualização de lista) */}
      {viewMode === 'list' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filtros</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-[#49943d] hover:text-[#3a7630] flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Busca */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Nome, telefone, observações..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="SCHEDULED">Agendado</option>
                <option value="IN_PROGRESS">Em Andamento</option>
                <option value="COMPLETED">Concluído</option>
                <option value="CANCELLED">Cancelado</option>
              </select>
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              <select
                value={filters.responsible_user_id}
                onChange={(e) => updateFilter('responsible_user_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os responsáveis</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente
              </label>
              <select
                value={filters.client_id}
                onChange={(e) => updateFilter('client_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os clientes</option>
                {clientsData?.data && Array.isArray(clientsData.data) ? clientsData.data.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.full_name}
                  </option>
                )) : null}
              </select>
            </div>

            {/* Data de */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de
              </label>
              <input
                type="datetime-local"
                value={filters.date_from}
                onChange={(e) => updateFilter('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Data até */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data até
              </label>
              <input
                type="datetime-local"
                value={filters.date_to}
                onChange={(e) => updateFilter('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Limpar filtros */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Lista de agendamentos - apenas quando viewMode é 'list' */}
      {viewMode === 'list' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3a7630] mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando agendamentos...</p>
          </div>
        ) : (
          <>
            {/* Tabela */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duração Prevista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Primeiro Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    // A API retorna { data: [...], pagination: {...} }
                    let data = appointmentsData?.data;
                    
                    // Se data não for array, pode estar em data.data
                    if (data && !Array.isArray(data) && data.data && Array.isArray(data.data)) {
                      data = data.data;
                    }
                    
                    if (!data) {
                      return (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                            Carregando dados...
                          </td>
                        </tr>
                      );
                    }
                    if (!Array.isArray(data)) {
                      console.error('Formato de dados inválido:', appointmentsData);
                      return (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                            Formato de dados inválido. Verifique o console.
                          </td>
                        </tr>
                      );
                    }
                    if (data.length === 0) {
                      return (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                            Nenhum agendamento encontrado
                          </td>
                        </tr>
                      );
                    }
                    return data.map((appointment) => (
                    <tr key={appointment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {appointment.client_name}
                            </div>
                            {appointment.appointment_type === 'PRESENTATION_3D' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                                🎨 Apresentação 3D
                              </span>
                            )}
                          </div>
                          {appointment.client_phone && (
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {appointment.client_phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(appointment.appointment_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.responsible_name || 'Não definido'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <select
                            value={appointment.status}
                            onChange={(e) => handleStatusChange(appointment, e.target.value)}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)} border-0 focus:ring-2 focus:ring-blue-500`}
                          >
                            <option value="SCHEDULED">Agendado</option>
                            <option value="IN_PROGRESS">Em Andamento</option>
                            <option value="COMPLETED">Concluído</option>
                            <option value="CANCELLED">Cancelado</option>
                          </select>
                          {/* Pipeline de Status - Tooltip */}
                          {appointmentStatusPipeline && getNextAppointmentStatuses(appointment.status).length > 0 && (
                            <div className="group relative">
                              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                              <div className="absolute left-0 top-full mt-1 z-10 hidden group-hover:block">
                                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[150px]">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Próximos:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {getNextAppointmentStatuses(appointment.status).map((status) => (
                                      <span
                                        key={status}
                                        className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700"
                                      >
                                        {formatAppointmentStatusName(status)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {appointment.duration_minutes} min
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {appointment.first_contact_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            #{appointment.first_contact_id}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {appointment.status === 'SCHEDULED' && (user?.id === appointment.responsible_user_id || ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role)) && (
                            <button
                              onClick={() => startSessionMutation.mutate({ appointment_id: appointment.id })}
                              className="text-green-600 hover:text-green-800"
                              title="Iniciar Atendimento"
                              disabled={startSessionMutation.isLoading}
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {appointment.status === 'IN_PROGRESS' && (user?.id === appointment.responsible_user_id || ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role)) && (
                            <button
                              onClick={() => setEndSessionConfirm(appointment.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Encerrar Atendimento"
                              disabled={endSessionMutation.isLoading}
                            >
                              <Square className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(appointment)}
                            className="text-[#49943d] hover:text-[#3a7630]"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role) && (
                            <button
                              onClick={() => navigate(`/levantamentos/novo?appointment_id=${appointment.id}`)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Criar Levantamento"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/agendamentos/editar/${appointment.id}`)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(appointment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Deletar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {(() => {
              const pagination = appointmentsData?.pagination || appointmentsData?.data?.pagination;
              if (!pagination) return null;
              
              return (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => updateFilter('page', Math.max(1, filters.page - 1))}
                      disabled={filters.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => updateFilter('page', filters.page + 1)}
                      disabled={filters.page >= pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando{' '}
                        <span className="font-medium">
                          {(filters.page - 1) * filters.limit + 1}
                        </span>{' '}
                        até{' '}
                        <span className="font-medium">
                          {Math.min(filters.page * filters.limit, pagination.total)}
                        </span>{' '}
                        de{' '}
                        <span className="font-medium">
                          {pagination.total}
                        </span>{' '}
                        resultados
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => updateFilter('page', Math.max(1, filters.page - 1))}
                          disabled={filters.page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <button
                          onClick={() => updateFilter('page', filters.page + 1)}
                          disabled={filters.page >= pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Próximo
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              );
            })()}
            </>
          )}
      </div>
      )}

      {/* Modal de detalhes */}
      {showDetails && selectedAppointment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Detalhes do Agendamento</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Tipo de Agendamento */}
                {selectedAppointment.appointment_type === 'PRESENTATION_3D' && (
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🎨</span>
                      <div>
                        <p className="text-sm font-medium text-pink-900">Apresentação de Projeto 3D</p>
                        <p className="text-xs text-pink-700">Este agendamento é referente à apresentação do projeto 3D para o cliente</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cliente</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedAppointment.client_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefone</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedAppointment.client_phone || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data/Hora</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedAppointment.appointment_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duração Prevista</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedAppointment.duration_minutes} minutos</p>
                  </div>
                  {/* Tempo de atendimento registrado (sessões) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Início Atendimento (real)</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedAppointment.last_session_start
                        ? formatTime(selectedAppointment.last_session_start)
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fim Atendimento (real)</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedAppointment.last_session_end
                        ? formatTime(selectedAppointment.last_session_end)
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tempo Registrado em Atendimento</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDuration(selectedAppointment.total_session_minutes)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Responsável</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedAppointment.responsible_name || 'Não definido'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedAppointment.status)}`}>
                      {getStatusText(selectedAppointment.status)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Endereço</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedAppointment.client_address}</p>
                </div>
                
                {selectedAppointment.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedAppointment.notes}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetails(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteConfirm}
        onConfirm={() => {
          if (deleteConfirm) deleteMutation.mutate(deleteConfirm);
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
        title="Excluir Agendamento"
        message="Tem certeza que deseja deletar este agendamento?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <ConfirmModal
        show={!!endSessionConfirm}
        onConfirm={() => {
          if (endSessionConfirm) endSessionMutation.mutate({ appointment_id: endSessionConfirm });
          setEndSessionConfirm(null);
        }}
        onCancel={() => setEndSessionConfirm(null)}
        title="Encerrar Atendimento"
        message="Tem certeza que deseja encerrar este atendimento?"
        confirmText="Encerrar"
        cancelText="Cancelar"
        type="warning"
      />
    </div>
  );
};

export default Appointments;